import React from 'react';
import { AlertCircle, ChevronRight, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { cn } from '../ui/utils';
import type { PerformanceTeamSummary } from '../../types/performance.types';
import { performancePillClass, reviewStatusTone } from '../../utils/performance';
import { InitialsAvatar, PerformanceEmptyState, PerformancePill } from './PerformancePrimitives';

interface TeamPanelProps {
  reports: PerformanceTeamSummary[];
  onOpenReport: (report: PerformanceTeamSummary) => void;
  onReviewReport: (report: PerformanceTeamSummary) => void;
  selectedReportId?: number | null;
}

export const TeamPanel: React.FC<TeamPanelProps> = ({
  reports,
  onOpenReport,
  onReviewReport,
  selectedReportId,
}) => {
  const [attentionOnly, setAttentionOnly] = React.useState(false);
  const rows = attentionOnly ? reports.filter((report) => report.needsAttention) : reports;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">My Team</h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {reports.length} direct report{reports.length === 1 ? '' : 's'} in this cycle.
            Goals are read-only here; you can open a report to read progress and add notes.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={attentionOnly} onCheckedChange={setAttentionOnly} />
          Needs attention only
        </label>
      </div>

      {!rows.length ? (
        <PerformanceEmptyState
          title={attentionOnly ? 'Nothing needs attention' : 'No direct reports in this cycle'}
          body={
            attentionOnly
              ? 'Everyone has goals, weights at 100% and a submitted goal plan.'
              : 'Your direct reports appear here once they are included in the performance schedule.'
          }
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-border bg-card md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-[11px] uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Person</th>
                  <th className="px-3 py-2.5 text-left font-medium">Goals</th>
                  <th className="px-3 py-2.5 text-left font-medium">Weighting</th>
                  <th className="px-3 py-2.5 text-left font-medium">Progress</th>
                  <th className="px-3 py-2.5 text-left font-medium">Mid-year</th>
                  <th className="px-3 py-2.5 text-left font-medium">End-of-year</th>
                  <th className="px-3 py-2.5 text-left font-medium">Flags</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((report) => (
                  <tr
                    key={report.user.id}
                    className={cn(
                      'border-t border-border transition-colors hover:bg-accent/40',
                      selectedReportId === report.user.id && 'bg-selection'
                    )}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <InitialsAvatar name={report.user.name} />
                        <div>
                          <div className="font-medium text-foreground">{report.user.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {report.user.jobRole}
                            {report.user.department ? ` · ${report.user.department}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-muted-foreground">{report.goalsCount}</td>
                    <td className="px-3 py-3">
                      <PerformancePill tone={report.weightTotal === 100 ? 'success' : 'warning'}>
                        {report.weightTotal}%
                      </PerformancePill>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${report.progress}%` }}
                          />
                        </div>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {report.progress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <PerformancePill tone={reviewStatusTone(report.reviews.mid)}>
                        {report.reviews.mid}
                      </PerformancePill>
                    </td>
                    <td className="px-3 py-3">
                      <PerformancePill tone={reviewStatusTone(report.reviews.eoy)}>
                        {report.reviews.eoy}
                      </PerformancePill>
                    </td>
                    <td className="px-3 py-3">
                      {report.flags.length ? (
                        <span className="inline-flex items-center gap-1 text-xs text-warning">
                          <AlertCircle className="h-3 w-3" /> {report.flags.join(' · ')}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onReviewReport(report)}
                        >
                          Review
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onOpenReport(report)}
                          aria-label={`Open ${report.user.name}'s goals`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2 md:hidden">
            {rows.map((report) => (
              <div
                key={report.user.id}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <InitialsAvatar name={report.user.name} />
                    <div>
                      <div className="text-sm font-semibold">{report.user.name}</div>
                      <div className="text-xs text-muted-foreground">{report.user.jobRole}</div>
                    </div>
                  </div>
                  <PerformancePill tone={report.weightTotal === 100 ? 'success' : 'warning'}>
                    {report.weightTotal}%
                  </PerformancePill>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-muted-foreground">Goals</p>
                    <p className="font-medium">{report.goalsCount}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Progress</p>
                    <p className="font-medium tabular-nums">{report.progress}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Plan</p>
                    <p className="font-medium">{report.planStatus}</p>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <PerformancePill tone={reviewStatusTone(report.reviews.mid)}>
                    Mid: {report.reviews.mid}
                  </PerformancePill>
                  <PerformancePill tone={reviewStatusTone(report.reviews.eoy)}>
                    EOY: {report.reviews.eoy}
                  </PerformancePill>
                </div>
                {report.flags.length ? (
                  <p className="mt-2 inline-flex items-center gap-1 text-xs text-warning">
                    <AlertCircle className="h-3 w-3" /> {report.flags.join(' · ')}
                  </p>
                ) : null}
                <div className="mt-3 flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => onReviewReport(report)}>
                    Review
                  </Button>
                  <Button size="sm" className="flex-1" onClick={() => onOpenReport(report)}>
                    Goals <ChevronRight className="ml-1 h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
